import { useCallback } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans, useTranslation, i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { CareCreditChip, CareCreditPatientData, CareCreditPatientStatus } from '@frontend/payments-care-credit';
import { theme } from '@frontend/theme';
import { currency, Text, useTooltip } from '@frontend/design-system';
import { useValidPhoneNumberFromInvoice } from '../../../../hooks/use-valid-phone-number-from-invoice';
import SunbitIcon from '../../../assets/sunbit.svg?react';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useValidEmailFromInvoice } from '../../../hooks/use-valid-email-from-invoice';
import { PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { PaymentMethodButton, PaymentMethodButtonProps } from '../../atoms/payment-method-button';

const customerPhoneNumberError = i18next.t('No valid phone number', { ns: 'payments' });
const customerEmailError = i18next.t("No email on file. Add one to customer's contact information to send request.", {
  ns: 'payments',
});

const ShareInEmail: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { firstValidEmail } = useValidEmailFromInvoice(invoice);

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.shareInEmail}` });

  const {
    Tooltip: EmailTooltip,
    triggerProps: EmailTooltipTriggerProps,
    tooltipProps: EmailTooltipProps,
  } = useTooltip();

  return (
    <PaymentMethodButton
      trackingId={trackingId}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.shareInEmail });
        onClick?.(e);
      }}
      iconName='email'
      {...(!firstValidEmail ? EmailTooltipTriggerProps : {})}
      {...props}
      disabled={invoiceIsPaid || !firstValidEmail || props.disabled}
    >
      {t('Share in Email')}
      <EmailTooltip {...EmailTooltipProps}>{customerEmailError}</EmailTooltip>
    </PaymentMethodButton>
  );
};
const ShareInMessage: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.shareInMessage}` });

  const { Tooltip: TtpTooltip, triggerProps: ttpTooltipTriggerProps, tooltipProps: ttpTooltipProps } = useTooltip();

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.shareInMessage });
        onClick?.(e);
      }}
      trackingId={trackingId}
      iconName='messaging'
      {...(!firstValidPhoneNumber ? ttpTooltipTriggerProps : {})}
      {...props}
      disabled={invoiceIsPaid || !firstValidPhoneNumber || props.disabled}
    >
      {t('Text-to-Pay')}
      <TtpTooltip {...ttpTooltipProps}>{customerPhoneNumberError}</TtpTooltip>
    </PaymentMethodButton>
  );
};

const SelectTerminal: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.selectTerminal}` });
  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.selectTerminal });
        onClick?.(e);
      }}
      iconName='credit-card'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {t('Pay at Terminal')}
    </PaymentMethodButton>
  );
};

const CardOnFile: React.FC<PaymentMethodButtonProps & { enableACHOnFile: boolean }> = ({
  onClick,
  enableACHOnFile,
  ...props
}) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.cardOnFile}` });

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.cardOnFile });
        onClick?.(e);
      }}
      iconName='user'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {enableACHOnFile ? t('Card/ACH on File') : t('Card on File')}
    </PaymentMethodButton>
  );
};

const ManualCardEntry: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({
    trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.manualCardEntry}`,
  });

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.manualCardEntry });
        onClick?.(e);
      }}
      iconName='edit-small'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {t('Manual Card Entry')}
    </PaymentMethodButton>
  );
};

const styles = {
  icon: css`
    margin-right: ${theme.spacing(1)};
    width: ${theme.spacing(2)};
    height: ${theme.spacing(2)};
  `,
  sunbitIcon: (isAcceptableSunbitAmount: boolean) =>
    css`
      ${!isAcceptableSunbitAmount && `opacity: 0.25;`};
      margin: 0 auto;
    `,
};

const MINIMUM_SUNBIT_AMOUNT = 6000;

const Sunbit: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const isAcceptableSunbitAmount = !!invoice?.billedAmount && invoice.billedAmount >= MINIMUM_SUNBIT_AMOUNT;

  const {
    Tooltip: SunbitTooltip,
    triggerProps: sunbitTooltipTriggerProps,
    tooltipProps: sunbitTooltipProps,
  } = useTooltip();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.sunbit}` });

  return (
    <PaymentMethodButton
      onClick={() => goNextStep({ orStep: PAYMENT_MULTI_STEPS.sunbit })}
      trackingId={trackingId}
      {...(!firstValidPhoneNumber ? sunbitTooltipTriggerProps : {})}
      {...props}
      disabled={invoiceIsPaid || !isAcceptableSunbitAmount || !firstValidPhoneNumber || props.disabled}
    >
      <div>
        <div>
          <SunbitIcon css={[styles.icon, styles.sunbitIcon(isAcceptableSunbitAmount)]} />
          <div>{t('Sunbit')}</div>
        </div>
        {!isAcceptableSunbitAmount && (
          <Text size='small' color='disabled'>
            {t('($60.00 min.)')}
          </Text>
        )}
        {<SunbitTooltip {...sunbitTooltipProps}>{customerPhoneNumberError}</SunbitTooltip>}
      </div>
    </PaymentMethodButton>
  );
};

const careCreditStyles = {
  amountLine: css`
    font-weight: ${theme.font.weight.regular};
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(0.5)};
    align-items: center;

    .icon {
      flex-shrink: 0;
    }
  `,
  italicLine: css`
    margin-top: ${theme.spacing(0.5)};
    font-style: italic;
    font-weight: ${theme.font.weight.regular};
    font-size: ${theme.font.size.small};
  `,
};

const CareCredit: React.FC<
  PaymentMethodButtonProps & { careCreditData?: CareCreditPatientData; isDeclined?: boolean }
> = ({ onClick, careCreditData, isDeclined, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    multiStep: { goNextStep },
    setCareCreditMessageType,
    invoiceIsPaid,
    isCareCreditLoading,
    isPersonValidForCareCredit,
    hasCareCreditResponseError,
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const applicantStatus = careCreditData?.status;

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${applicantStatus}` });

  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    placement: 'bottom-start',
  });

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (applicantStatus === 'approved') {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditCollect,
        });
      } else if (applicantStatus === 'pre-approved') {
        setCareCreditMessageType('pre-approved-link');
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditMessageDisclosure,
        });
      } else if (applicantStatus === 'not-enrolled') {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditEnroll,
        });
      } else {
        console.warn('Unknown applicant status', applicantStatus);
      }
      onClick?.(e);
    },
    [onClick, applicantStatus, goNextStep, setCareCreditMessageType]
  );

  const showCareCreditAmount = ['approved', 'pre-approved'].includes(applicantStatus || '');

  const statusWithErrors: CareCreditPatientStatus | undefined =
    hasCareCreditResponseError || !isPersonValidForCareCredit ? 'unknown-or-error' : applicantStatus;

  const isDeclinedOrByStatus = isDeclined || statusWithErrors === 'unknown-or-error';

  let tooltipMessage = '';
  if (statusWithErrors === 'unknown-or-error') {
    tooltipMessage = t('Unable to retrieve CareCredit eligibility status at this time');
  } else if (applicantStatus === 'not-enrolled') {
    tooltipMessage = t('Customer must be approved for CareCredit to use this option.');
  } else if (!firstValidPhoneNumber && applicantStatus === 'pre-approved') {
    tooltipMessage = customerPhoneNumberError;
  }

  return (
    <PaymentMethodButton
      trackingId={trackingId}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        handleClick(e);
      }}
      iconName='care-credit'
      chip={
        !isCareCreditLoading &&
        applicantStatus !== 'not-enrolled' && <CareCreditChip status={isDeclined ? 'declined' : statusWithErrors} />
      }
      tooltip={tooltipMessage}
      {...props}
      loading={isCareCreditLoading}
      disabled={
        props.disabled ||
        invoiceIsPaid ||
        isDeclinedOrByStatus ||
        isCareCreditLoading ||
        (!firstValidPhoneNumber && applicantStatus === 'pre-approved') ||
        applicantStatus === 'not-enrolled'
      }
    >
      <div>{t('CareCredit')}</div>
      {showCareCreditAmount && (
        <>
          <div>
            <div css={careCreditStyles.amountLine}>
              {careCreditData?.status === 'approved' ? (
                <span>
                  <Trans
                    t={t}
                    i18nKey='Available: <b>{{amount}}</b>'
                    values={{ amount: currency(careCreditData?.amount || 0, { decimals: 0 }) }}
                    components={{ b: <b /> }}
                  />
                </span>
              ) : (
                <span>
                  <Trans
                    t={t}
                    i18nKey='Credit Limit: <b>{{amount}}</b>'
                    values={{ amount: currency(careCreditData?.amount || 0, { decimals: 0 }) }}
                    components={{ b: <b /> }}
                  />
                </span>
              )}

              {careCreditData?.asOfDate && careCreditData?.status !== 'pre-approved' && (
                <>
                  <span className='icon' {...triggerProps}>
                    <Icon name='info' size={16} />
                  </span>
                  <Tooltip {...tooltipProps}>
                    {t('Last updated {{date}} at {{time}}', {
                      date: dayjs(careCreditData?.asOfDate).format('MMM DD, YYYY'),
                      time: dayjs(careCreditData?.asOfDate).format('h:mm A'),
                    })}
                  </Tooltip>
                </>
              )}
            </div>
            {careCreditData?.status === 'pre-approved' && (
              <div css={careCreditStyles.italicLine}>{t('(Subject to approval)')}</div>
            )}
          </div>
        </>
      )}
    </PaymentMethodButton>
  );
};

export const PaymentMethodButtons = {
  ShareInEmail,
  ShareInMessage,
  SelectTerminal,
  CardOnFile,
  ManualCardEntry,
  Sunbit,
  CareCredit,
};
